import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Divider, Icon, Select, Switch, Tooltip, message, notification } from "antd";
import OBCSearchInput from "../OBC/OBCSearchInput";
import "../../style/utuSuspectsTab.scss";
import {
    getUtuSuspectsV2,
    setUTUSuspectSearchQueryFilter,
    selectUTUSuspect,
    routeSelected,
    setUTUSuspectDateFilter,
    selectUTUSuspectSession,
    requestPlaylistPosition,
    goToBounds,
} from "../../redux/actions";
import { useDispatch, useSelector } from "react-redux";
import { VariableSizeList } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import OBCSpinner from "../util/OBC";
import _ from "lodash";
import DayPicker from "react-day-picker";
import OBCButton from "../OBC/OBCButton";
import { CalendarOutlined } from "@ant-design/icons";
import moment from "moment";
import { faExclamationCircle, faSortAmountDown } from "@fortawesome/free-solid-svg-icons";
import { convertToTimezone, convertTsToMilliseconds } from "components/util/TimezoneUtils";
import OBCToggleButton from "components/OBC/OBCToggleButton";
import Highlighter from "react-highlight-words";
import { binarySearch } from "components/util/PlaylistUtils";

const sessionsSelector = (state) => state.sessions;
const utuSuspectsSelector = (state) => state.utuSuspects.data;
const selectedSuspectSelector = (state) => state.utuSuspects.selectedSuspect;
const searchQueryFilterSelector = (state) => state.utuSuspects.searchQueryFilter;
const dateFiltersSelector = (state) => state.utuSuspects.dateFilter;
const fullyLoadedSelector = (state) => state.utuSuspects.fullyLoaded;
const userConfigSelector = (state) => state.userDetails.userConfig;
const playlistDataSelector = (state) => state.playlist.data;
const railDataObjectSelector = (state) => state.railInspection.railInspectionImages.data;
const lastSourceIndexSelector = (state) => state.playlist.position.sourceIndex;

const GUTTER_SIZE = 30;
const ISSUE_ITEM_HEIGHT_CLOSED = 250;

const SORT_OPTIONS = [
    {
        value: "high_priority",
        label: (
            <span>
                Priority <FontAwesomeIcon icon={faExclamationCircle} />
            </span>
        ),
        short_title: "Priority",
        description: "fault priority",
    },
    {
        value: "observations_count_desc",
        label: (
            <span>
                Observation Count <FontAwesomeIcon icon={faSortAmountDown} />
            </span>
        ),
        short_title: "Observation Count",
        description: "fault observation count",
    },
    {
        value: "last_observed_date",
        label: (
            <span>
                Observation date <FontAwesomeIcon icon={faSortAmountDown} />
            </span>
        ),
        short_title: "Observation Date",
        description: "last observation date",
    },
];

const UtuSuspectsTab = () => {
    const dispatch = useDispatch();
    const allUtuSuspects = useSelector(utuSuspectsSelector);
    const listRef = useRef();
    const sizeMap = useRef({});

    const [gettingSuspects, setGettingSuspects] = useState(false);
    const [sortValue, setSortValue] = useState("high_priority");
    const [sortDescription, setSortDescription] = useState("highest priority faults first");
    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const [showUnmatchedSuspects, setShowUnmatchedSuspects] = useState(false);

    const sessions = useSelector(sessionsSelector);
    const searchQuery = useSelector(searchQueryFilterSelector);
    const selectedSuspect = useSelector(selectedSuspectSelector);
    const dateFilter = useSelector(dateFiltersSelector);
    const userConfig = useSelector(userConfigSelector);
    const fullyLoaded = useSelector(fullyLoadedSelector);

    const [requestMapBoundsForSession, setRequestMapBoundsForSession] = useState(null);

    const dateFrom = useMemo(() => (dateFilter.from ? new Date(convertTsToMilliseconds(dateFilter.from)) : null), [dateFilter.from]);
    const dateTo = useMemo(() => (dateFilter.to ? new Date(convertTsToMilliseconds(dateFilter.to)) : null), [dateFilter.to]);
    const modifiers = { start: dateFrom, end: dateTo };

    const onSearchQueryChanged = (value, fullyLoaded) => {
        dispatch(setUTUSuspectSearchQueryFilter(value));
    };

    const newFilteredUtuSuspectsList = useMemo(() => {
        const normalize = (val) => String(val ?? "").toLowerCase();
        let newList = Array.isArray(allUtuSuspects) ? [...allUtuSuspects] : [];
        return newList.map((suspect) => {
            return {
                ...suspect,
                session_id: normalize(suspect.session_id),
                test_unit: normalize(suspect.test_unit),
                elr: normalize(suspect.elr),
                track_id: normalize(suspect.track_id),
                rail: normalize(suspect.rail),
                miles: normalize(suspect.miles),
                yards: normalize(suspect.yards),
                uid: normalize(suspect.uid),
                run_detail_ts: new Date(suspect.run_detail).getTime(),
            };
        });
    }, [allUtuSuspects]);

    // when session is selected from suspect observations, we need to go to its bounds on the map
    useEffect(() => {
        const selectedSessionData = _.get(sessions, [requestMapBoundsForSession], null);
        if (requestMapBoundsForSession && selectedSessionData) {
            if (selectedSessionData && selectedSessionData.bounds) {
                dispatch(goToBounds(selectedSessionData.bounds));
            }
            setRequestMapBoundsForSession(null);
        }
    }, [dispatch, requestMapBoundsForSession, sessions]);

    const filteredUtuSuspects = useMemo(() => {
        let newList = [];

        let dateFrom = null;
        let dateTo = null;

        if (dateFilter.from && dateFilter.to) {
            dateFrom = convertTsToMilliseconds(dateFilter.from);
            dateTo = convertTsToMilliseconds(dateFilter.to);
        }

        _.forEach(newFilteredUtuSuspectsList, (suspect) => {
            let includeSuspect = true;

            // filter by date
            if (dateFrom && dateTo) {
                const runDetail = suspect.run_detail_ts || null;
                if (!runDetail || runDetail < dateFrom || runDetail > dateTo) {
                    includeSuspect = false;
                }
            }

            // filter by search query
            if (searchQuery && searchQuery.length > 0) {
                const searchQueryList = String(searchQuery)
                    .split(" ")
                    .map((item) => item.toLocaleLowerCase());
                if (
                    !searchQueryList.every((term) => {
                        return (
                            suspect.session_id.includes(term) ||
                            suspect.test_unit.includes(term) ||
                            suspect.elr.includes(term) ||
                            suspect.track_id.includes(term) ||
                            suspect.rail.includes(term) ||
                            suspect.miles.includes(term) ||
                            suspect.yards.includes(term) ||
                            suspect.uid.includes(term)
                        );
                    })
                ) {
                    includeSuspect = false;
                }
            }

            // filter unmatched suspects
            if (!showUnmatchedSuspects) {
                includeSuspect = includeSuspect && suspect.session_id;
            }

            if (includeSuspect) {
                newList.push(suspect);
            }
        });

        return newList;
    }, [newFilteredUtuSuspectsList, dateFilter, searchQuery, showUnmatchedSuspects]);

    const groupedAllSuspectsCount = useMemo(() => {
        return _.keys(_.groupBy(allUtuSuspects, (suspect) => suspect.uid)).length;
    }, [allUtuSuspects]);

    const groupedUtuSuspects = useMemo(() => {
        // sort by priority first so when we group by uid the highest priority will be first in the list
        const sortedList = _.orderBy(filteredUtuSuspects, "run_detail_ts", "desc");

        let groupedSuspects = _.groupBy(sortedList, (suspect) => suspect.uid);
        let uniqueSuspects = Object.keys(groupedSuspects).map((key) => {
            // group master object to be latest suspect
            let newObj = groupedSuspects[key][0];
            // add all suspects with session ID under all_suspects key for this uid
            const suspectsWithSessionID = groupedSuspects[key].filter((suspect) => suspect.session_id);
            newObj.all_suspects = suspectsWithSessionID; // only include suspects with session_id
            return newObj;
        });

        // sort by current sort option
        if (sortValue === "high_priority") {
            uniqueSuspects = _.orderBy(uniqueSuspects, ["priority", "run_detail_ts"], ["asc", "desc"]);
        } else if (sortValue === "low_priority") {
            uniqueSuspects = _.orderBy(uniqueSuspects, ["priority", "run_detail_ts"], ["desc", "desc"]);
        } else if (sortValue === "observations_count_desc") {
            uniqueSuspects = _.orderBy(uniqueSuspects, [(suspect) => suspect.all_suspects.length || 0, "run_detail_ts"], ["desc", "desc"]);
        } else if (sortValue === "observations_count_asc") {
            uniqueSuspects = _.orderBy(uniqueSuspects, [(suspect) => suspect.all_suspects.length || 0, "run_detail_ts"], ["asc", "desc"]);
        } else if (sortValue === "last_observed_date") {
            uniqueSuspects = _.orderBy(uniqueSuspects, ["run_detail_ts"], ["desc"]);
        } else if (sortValue === "first_observed_date") {
            uniqueSuspects = _.orderBy(uniqueSuspects, ["run_detail_ts"], ["asc"]);
        }

        return uniqueSuspects;
    }, [filteredUtuSuspects, sortValue]);

    // once all suspects are loaded, we'll perform filtering/sorting on the client side
    useEffect(() => {
        if (!fullyLoaded) {
            setGettingSuspects(true);
            dispatch(
                getUtuSuspectsV2(sortValue, searchQuery, dateFilter.from, dateFilter.to, 0, (success) => {
                    setGettingSuspects(false);
                    if (!success) {
                        notification.error({
                            message: "Error loading UTU Suspects",
                        });
                    }
                }),
            );
        }
    }, [dispatch, sortValue, searchQuery, dateFilter, fullyLoaded]);

    useEffect(() => {
        let sort_description = _.find(SORT_OPTIONS, { value: sortValue });
        setSortDescription(sort_description.description);
    }, [sortValue]);

    const renderDatePicker = () => {
        const today = new Date();
        return (
            <>
                <DayPicker
                    className="Selectable"
                    selectedDays={[dateFrom, { from: dateFrom, to: dateTo }]}
                    modifiers={modifiers}
                    onDayClick={handleDayClick}
                    disabledDays={{ after: today }}
                />
                <div className="DatePickerButtons">
                    <OBCButton
                        className="ButtonReset"
                        disabled={dateFilter.from ? false : true}
                        onClick={() => dispatch(setUTUSuspectDateFilter({ from: null, to: null }))}>
                        Reset
                    </OBCButton>
                    <OBCButton
                        className="ButtonOk"
                        variant="secondary"
                        onClick={() => setDatePickerOpen(false)}>
                        Close
                    </OBCButton>
                </div>
            </>
        );
    };

    const sortByOptions = useMemo(() => {
        let options = [];

        options = SORT_OPTIONS.map((option) => (
            <Select.Option
                key={option.value}
                value={option.value}
                label={
                    <>
                        <FontAwesomeIcon
                            icon={faSortAmountDown}
                            style={{ marginRight: "5px" }}
                        />
                        {option.short_title}
                    </>
                }>
                {option.label}
            </Select.Option>
        ));

        return options;
    }, []);

    const handleDayClick = (day) => {
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        if (day > today) {
            return;
        }

        const currentRange = {
            from: new Date(convertTsToMilliseconds(dateFilter.from)),
            to: new Date(convertTsToMilliseconds(dateFilter.to)),
        };

        const range = DayPicker.DateUtils.addDayToRange(day, currentRange);
        if (!dateFilter.to) {
            range.to = day;
            range.from = day;
        }
        const newDateFilter = {
            to: moment(range.to).endOf("day").unix(),
            from: moment(range.from).startOf("day").unix(),
        };

        dispatch(setUTUSuspectDateFilter(newDateFilter));
    };

    const setSize = useCallback((index, size) => {
        sizeMap.current = { ...sizeMap.current, [index]: size };
        listRef.current.resetAfterIndex(index);
    }, []);

    const getItemSize = (index) => {
        if (sizeMap.current[index]) {
            return Math.max(sizeMap.current[index] + GUTTER_SIZE, ISSUE_ITEM_HEIGHT_CLOSED);
        }
        return ISSUE_ITEM_HEIGHT_CLOSED;
    };

    const Row = useCallback(
        ({ index, style }) => {
            const suspectID = _.get(groupedUtuSuspects, [index, "uid"], null);
            const suspect = groupedUtuSuspects[index] || [];

            return (
                <div
                    style={{
                        ...style,
                        left: style.left + 2,
                    }}>
                    <SuspectItem
                        key={`${suspectID}`}
                        index={index}
                        suspect={suspect}
                        setSize={setSize}
                        selected={suspectID === selectedSuspect.uid}
                        setRequestMapBoundsForSession={setRequestMapBoundsForSession}
                        minHeight={ISSUE_ITEM_HEIGHT_CLOSED}
                        userConfig={userConfig}
                    />
                </div>
            );
        },
        [groupedUtuSuspects, setSize, selectedSuspect, userConfig],
    );

    return (
        <div className="utuSuspectsContentTab">
            <div className="TopContainer">
                <div className="utuFilterContainer">
                    <div className="LeftColumn">
                        <Tooltip title="Sort suspects">
                            <div className="SortByPrioritySelect">
                                <span>Sort</span>
                                <Select
                                    optionLabelProp="label"
                                    value={sortValue}
                                    onChange={setSortValue}
                                    dropdownMatchSelectWidth={false}>
                                    {sortByOptions}
                                </Select>
                            </div>
                        </Tooltip>
                        <Tooltip title="Filter suspects by date range">
                            <div className="DateFilterSelect">
                                <span>Date</span>
                                <Tooltip
                                    overlayClassName="IssueTabDatePicker"
                                    overlayStyle={{ opacity: 1 }}
                                    title={renderDatePicker(dateFrom, dateTo, modifiers)}
                                    visible={datePickerOpen}
                                    placement="bottomLeft"
                                    trigger="click"
                                    onVisibleChange={(visible) => setDatePickerOpen(visible)}>
                                    <OBCToggleButton
                                        active={datePickerOpen}
                                        toggled={dateFrom && dateTo}
                                        variant={dateFilter.from ? "primary" : "ghost"}
                                        onClick={() => setDatePickerOpen(!datePickerOpen)}>
                                        <CalendarOutlined
                                            classID="icon"
                                            style={{ fontSize: "15px" }}
                                        />
                                    </OBCToggleButton>
                                </Tooltip>
                            </div>
                        </Tooltip>
                        {/* only show suspects with session ID */}
                        <div className="UnmatchedUtuSuspectsSwitch">
                            <Switch
                                checkedChildren={<Icon type="check" />}
                                unCheckedChildren={<Icon type="close" />}
                                onChange={(checked) => setShowUnmatchedSuspects(checked)}
                                checked={showUnmatchedSuspects}
                            />
                            <Tooltip
                                title="Enable to show unmatched suspects in the list"
                                overlayStyle={{ minWidth: "310px" }}>
                                <span
                                    className="UnmatchedFilterText"
                                    onClick={() => setShowUnmatchedSuspects((prevState) => !prevState)}>
                                    Show Unmatched
                                </span>
                            </Tooltip>
                        </div>
                    </div>
                    <div className="RightColumn">
                        <Tooltip
                            title="Search suspects by UID, Session ID, Test Unit, ELR, Track ID, Rail, Miles or Yards (supports multiple terms separated by space)"
                            mouseEnterDelay={1}
                            placement="left"
                            trigger="hover">
                            <OBCSearchInput
                                className="UTUSuspectsFilterSearchInput"
                                placeholder="Search suspects"
                                onChange={(value) => {
                                    onSearchQueryChanged(value, fullyLoaded);
                                }}
                                allowClear={true}
                                value={searchQuery}
                            />
                        </Tooltip>
                    </div>
                </div>
                <div className="CountLabel">
                    Showing <span className={`${groupedUtuSuspects.length && "Value"}`}>{groupedUtuSuspects.length}</span> of {groupedAllSuspectsCount}{" "}
                    ultrasonic suspects, sorted by {sortDescription}
                </div>
            </div>
            {gettingSuspects ? (
                <div className="UTUTabLoading">
                    <OBCSpinner
                        size={70}
                        speed={3}
                        color={"#e8dfff"}
                    />
                </div>
            ) : (
                <div
                    className="UtuSuspectsList"
                    id="ScrollList">
                    {_.isEmpty(groupedUtuSuspects) && searchQuery && (
                        <div className="NoData">
                            <div>No matching suspects found</div>
                            <div>
                                for: <strong>{searchQuery}</strong>
                            </div>
                        </div>
                    )}
                    <AutoSizer className="suspectsAutosizer">
                        {({ height, width }) => (
                            <VariableSizeList
                                height={height}
                                width={width - 5}
                                style={{ overflowX: "hidden" }}
                                itemSize={getItemSize}
                                ref={listRef}
                                itemCount={_.keys(groupedUtuSuspects).length}>
                                {Row.bind(this)}
                            </VariableSizeList>
                        )}
                    </AutoSizer>
                </div>
            )}
        </div>
    );
};

const SuspectItem = ({ index, suspect, setSize, windowWidth, minHeight, selected, userConfig, setRequestMapBoundsForSession }) => {
    const dispatch = useDispatch();
    const ref = useRef();
    const [UIDCopyText, setUIDCopyText] = useState("Click to copy");

    const searchQuery = useSelector(searchQueryFilterSelector);
    const selectedSuspect = useSelector(selectedSuspectSelector);
    const railData = useSelector(railDataObjectSelector);
    const sourceIndex = useSelector(lastSourceIndexSelector);
    const playlistData = useSelector(playlistDataSelector);

    const selectedSessionID = selectedSuspect.sessionID;
    const loadedUID = selectedSuspect.loadedUID;

    const setIssueSize = () => {
        const size = ref.current.getBoundingClientRect().height - 20;
        setSize(index, size);
    };

    useEffect(() => {
        setIssueSize();
    }, [setSize, index, windowWidth]);

    const openNotification = () => {
        notification.info({
            message: "No matched session",
            description: "There is no session to open as this fault has not been matched to one",
        });
    };

    const suspectStatus = () => {
        if (suspect.priority === 1) {
            let colour = "red";
            let icon = faExclamationCircle;
            let text = "High Priority";

            return (
                <div className={`StatusDiv`}>
                    <FontAwesomeIcon
                        icon={icon}
                        className={`Icon ${colour}`}
                    />
                    <p className="StatusText">{text}</p>
                </div>
            );
        }
    };

    const onSuspectItemClick = (disabled) => {
        if (disabled) {
            message.info("No matched sessions for this suspect");
            return;
        }

        if (!selected) {
            dispatch(selectUTUSuspect(suspect.uid));
        } else {
            dispatch(selectUTUSuspect(null));
        }
    };

    const parseDate = (date) => {
        const timeDate = new Date(date); // here date is actually string like: 2024-08-19
        return convertToTimezone(timeDate, userConfig.convert_to_utc, { year: "numeric", month: "long", day: "numeric" }, false);
    };

    const handleUIDHovered = (e) => {
        if (e && UIDCopyText === "Copied!") {
            setUIDCopyText("Click to copy");
        }
    };

    const handleUIDClick = (e, id) => {
        e.preventDefault();
        e.stopPropagation();
        navigator.clipboard.writeText(id);
        setUIDCopyText("Copied!");
    };

    const disabled = suspect.all_suspects.length === 0;

    const latestSuspectRunDetail = useMemo(() => {
        if (suspect.all_suspects.length === 0) {
            return suspect.run_detail;
        }

        const sortedList = _.orderBy(suspect.all_suspects, [(suspect) => suspect.run_detail || 0], ["desc"]);
        return sortedList[0].run_detail;
    }, [suspect]);

    return (
        <>
            <div
                onClick={() => onSuspectItemClick(disabled)}
                className={`IssueListItem ${selected ? "Open" : "Closed"} ${disabled ? "disabled" : ""}`}
                style={{ minHeight }}
                ref={ref}>
                <div className="SuspectHeader">
                    <div className="SuspectIdContainer">
                        <strong>UID:</strong>
                        <Tooltip
                            title={UIDCopyText}
                            onVisibleChange={(e) => handleUIDHovered(e)}>
                            <div
                                className=""
                                onClick={(e) => handleUIDClick(e, suspect.uid)}>
                                <div className="SuspectID">
                                    <Highlighter
                                        highlightClassName="ObcTextHighlight"
                                        searchWords={String(searchQuery).split(" ")}
                                        autoEscape={true}
                                        textToHighlight={String(suspect.uid)}
                                    />
                                </div>
                            </div>
                        </Tooltip>
                    </div>
                    <div className="SuspectHeaderRight">
                        {suspectStatus()}
                        <div className="SuspectsCount">
                            Total count: <div className="SuspectsCountTag">{suspect.all_suspects.length}</div>
                        </div>
                    </div>
                </div>
                <div className="SuspectBody">
                    <span>
                        <strong>Session ID: </strong>
                        {suspect.session_id ? (
                            <Highlighter
                                highlightClassName="ObcTextHighlight"
                                searchWords={String(searchQuery).split(" ")}
                                autoEscape={true}
                                textToHighlight={String(suspect.session_id)}
                            />
                        ) : (
                            "Not matched to a session"
                        )}
                    </span>
                    <span>
                        <strong>Highest Priority:</strong> <span className={`${suspect.priority === 1 ? "highPriority" : ""}`}>{suspect.priority}</span>
                    </span>
                    <span>
                        <strong>Latest Observed:</strong> {parseDate(latestSuspectRunDetail)}
                    </span>
                    <span>
                        <strong>Test Unit: </strong>
                        <Highlighter
                            highlightClassName="ObcTextHighlight"
                            searchWords={String(searchQuery).split(" ")}
                            autoEscape={true}
                            textToHighlight={String(suspect.test_unit)}
                        />
                    </span>
                    <span>
                        <strong>Type:</strong> {suspect.suspect_type}
                    </span>
                    <span>
                        <strong>Comment:</strong> {suspect.suspect_comment}
                    </span>
                </div>
                <div className="SuspectFooter">
                    <div className="SuspectFooterContainer">
                        <div className="SuspectPosition">
                            <span>
                                ELR:{" "}
                                <strong>
                                    <Highlighter
                                        highlightClassName="ObcTextHighlight"
                                        searchWords={String(searchQuery).split(" ")}
                                        autoEscape={true}
                                        textToHighlight={String(suspect.elr).toUpperCase()}
                                    />
                                </strong>
                            </span>
                            <span>
                                Track:{" "}
                                <strong>
                                    <Highlighter
                                        highlightClassName="ObcTextHighlight"
                                        searchWords={String(searchQuery).split(" ")}
                                        autoEscape={true}
                                        textToHighlight={String(suspect.track_id)}
                                    />
                                </strong>
                            </span>
                            <span>
                                Miles:{" "}
                                <strong>
                                    <Highlighter
                                        highlightClassName="ObcTextHighlight"
                                        searchWords={String(searchQuery).split(" ")}
                                        autoEscape={true}
                                        textToHighlight={String(suspect.miles)}
                                    />
                                </strong>
                            </span>
                            <span>
                                Yards:{" "}
                                <strong>
                                    <Highlighter
                                        highlightClassName="ObcTextHighlight"
                                        searchWords={String(searchQuery).split(" ")}
                                        autoEscape={true}
                                        textToHighlight={String(suspect.yards)}
                                    />
                                </strong>
                            </span>
                            <span>
                                Rail:{" "}
                                <strong>
                                    <Highlighter
                                        highlightClassName="ObcTextHighlight"
                                        searchWords={String(searchQuery).split(" ")}
                                        autoEscape={true}
                                        textToHighlight={String(suspect.rail).toUpperCase()}
                                    />
                                </strong>
                            </span>
                        </div>
                    </div>
                </div>
                {selected && (
                    <div className="UtuSuspectsObservations">
                        <Divider>Observations</Divider>
                        <div className="UtuSuspectsList">
                            {suspect.all_suspects ? (
                                _.map(suspect.all_suspects, (item, idx) => {
                                    return (
                                        <div
                                            key={idx}
                                            className={`GroupedSuspectItem ${suspect.uid === loadedUID && item.session_id === selectedSessionID ? "selected" : ""}`}
                                            style={{ cursor: item.session_id ? "pointer" : "not-allowed" }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (item.session_id) {
                                                    // if this session is already selected in player and suspect uid is also loaded then just return
                                                    // otherwise we need to update position in playlist to this session
                                                    if (item.session_id === selectedSessionID) {
                                                        let timestamp = item.image_ts;

                                                        // already selected in playlist so just return
                                                        if (suspect.uid === loadedUID) return;

                                                        let videoPlaylistIndex = _.findIndex(railData, { timestamp: timestamp });

                                                        // new binary search implementation for finding index using routeLocationsV2 cache
                                                        if (playlistData.route_locations_v2) {
                                                            if (timestamp < 9999999999) {
                                                                timestamp *= 1000;
                                                            }
                                                            videoPlaylistIndex = binarySearch(timestamp, railData, (image) => image.timestamp);
                                                        }
                                                        if (videoPlaylistIndex > -1) {
                                                            dispatch(requestPlaylistPosition(true, false, true, sourceIndex, videoPlaylistIndex, timestamp));
                                                            dispatch(selectUTUSuspectSession(item.session_id, suspect.uid));
                                                        }
                                                        return;
                                                    }

                                                    // we are about to select new session, we need to deselect current first
                                                    dispatch(routeSelected(null));

                                                    // wait for the state to update before selecting new session
                                                    Promise.resolve().then(() => {
                                                        dispatch(routeSelected(item.session_id, item.image_ts, undefined, undefined, 0));
                                                        dispatch(selectUTUSuspectSession(item.session_id, suspect.uid));
                                                    });
                                                    setRequestMapBoundsForSession(parseInt(item.session_id));
                                                } else {
                                                    // If no valid videoPlaylistIndex is found, open a notification
                                                    openNotification();
                                                }
                                            }}>
                                            <span>
                                                <strong>Session ID: </strong>
                                                {item.session_id ? (
                                                    <Highlighter
                                                        highlightClassName="ObcTextHighlight"
                                                        searchWords={String(searchQuery).split(" ")}
                                                        autoEscape={true}
                                                        textToHighlight={String(item.session_id)}
                                                    />
                                                ) : (
                                                    "Not matched to a session"
                                                )}
                                            </span>
                                            <div className="OpenSuspectInNewTabButton">
                                                <Button
                                                    type="link"
                                                    size="small"
                                                    icon="export"
                                                    disabled={!item.session_id}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                        if (item.session_id) {
                                                            let url = `${window.location.href}rail-inspection/${item.session_id}/${item.image_ts}?selected=utu_suspects`;

                                                            const options = item.uid ? JSON.stringify({ suspect_id: item.uid }) : null;
                                                            if (options) {
                                                                const encoded = encodeURIComponent(options);
                                                                url += `&options=${encoded}`;
                                                            }
                                                            window.open(url, "_blank");
                                                        } else {
                                                            openNotification();
                                                        }
                                                    }}>
                                                    Open in New Tab
                                                </Button>
                                            </div>
                                            {item.priority === 1 ? (
                                                <span>
                                                    <strong>Priority:</strong> <span className="highPriority">{item.priority}</span>
                                                </span>
                                            ) : (
                                                <span>
                                                    <strong>Priority:</strong> {item.priority}
                                                </span>
                                            )}
                                            <span>
                                                <strong>Run Date:</strong> {parseDate(item.run_detail)}
                                            </span>
                                            <span>
                                                <strong>Pulse Count:</strong> {item.pulse_count}
                                            </span>
                                            <span>
                                                <strong>Comment:</strong> {item.suspect_comment}
                                            </span>
                                        </div>
                                    );
                                })
                            ) : (
                                <div>No observations found</div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default UtuSuspectsTab;
